import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags/feature-flag.constants';
import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { filter } from 'rxjs';

const BRANCH_STORAGE_PREFIX = 'alpha-branch:';

@Injectable({ providedIn: 'root' })
export class BranchStateService {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly isBrowser = isPlatformBrowser(this.platformId);
    private readonly featureFlagsService = inject(FeatureFlagsService);

    private readonly _currentBranch = signal<string>('main');
    readonly currentBranch = this._currentBranch.asReadonly();

    constructor() {
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            this._currentBranch.set(params['branch'] || 'main');
        });

        this.setupNavigationInterceptor();
    }

    /** Returns branch for API calls. Returns null for 'main' (default). */
    getBranch(): string {
        const branch = this._currentBranch();
        return branch;
    }

    /** Set branch synchronously (used by resolver before config fetches). */
    setBranch(branch: string): void {
        this._currentBranch.set(branch || 'main');
    }

    /** Save branch preference for a project to localStorage */
    saveBranch(projectId: string, branch: string): void {
        if (!this.isBrowser || !projectId) return;
        try {
            localStorage.setItem(`${BRANCH_STORAGE_PREFIX}${projectId}`, branch);
        } catch {
            // localStorage full or unavailable - fail silently
        }
    }

    /** Load saved branch for a project from localStorage, returns null if none saved */
    loadBranch(projectId: string): string | null {
        if (!this.isBrowser || !projectId) return null;
        try {
            return localStorage.getItem(`${BRANCH_STORAGE_PREFIX}${projectId}`);
        } catch {
            return null;
        }
    }

    /** Clear saved branch for a project */
    clearBranch(projectId: string): void {
        if (!this.isBrowser || !projectId) return;
        try {
            localStorage.removeItem(`${BRANCH_STORAGE_PREFIX}${projectId}`);
        } catch {
            // ignore
        }
    }

    private setupNavigationInterceptor(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                this.handleNavigation(event);
            });
    }

    private handleNavigation(event: NavigationStart): void {
        // Skip branch injection when change tracking is disabled
        if (!this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_KEYS.CHANGE_TRACKING)) {
            return;
        }

        const targetUrl = this.router.parseUrl(event.url);
        const targetPath = targetUrl.root.children['primary']?.segments.map((s) => s.path).join('/') || '';

        // Only handle project routes
        if (!targetPath.startsWith('project/')) {
            return;
        }

        const targetBranch = targetUrl.queryParams['branch'];

        // If target already has branch, nothing to do
        if (targetBranch) {
            return;
        }

        // Extract projectId from path (project/:projectId/...)
        const pathSegments = targetPath.split('/');
        const targetProjectId = pathSegments[1];

        // Try to restore saved branch for this project, fallback to current signal, then 'main'
        const savedBranch = this.loadBranch(targetProjectId);
        const branchToUse = savedBranch || this._currentBranch() || 'main';

        // Add branch to target URL and redirect
        targetUrl.queryParams = { ...targetUrl.queryParams, branch: branchToUse };
        this.router.navigateByUrl(targetUrl);
    }
}
